const NcWishlistStore = {
  key: 'nc-wishlist',

  getAll() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch {
      return [];
    }
  },

  save(items) {
    try {
      localStorage.setItem(this.key, JSON.stringify(items));
    } catch {}
  },

  has(handle) {
    return this.getAll().some((item) => item.handle === handle);
  },

  add(handle) {
    const items = this.getAll();
    if (items.some((item) => item.handle === handle)) return;
    items.push({ handle, addedAt: Date.now() });
    this.save(items);
    publish(PUB_SUB_EVENTS.wishlistUpdate, { handle, action: 'add', count: items.length });
  },

  remove(handle) {
    const items = this.getAll().filter((item) => item.handle !== handle);
    this.save(items);
    publish(PUB_SUB_EVENTS.wishlistUpdate, { handle, action: 'remove', count: items.length });
  },

  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  count() {
    return this.getAll().length;
  },
};

window.NcWishlistStore = NcWishlistStore;

class NcWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.button = this.querySelector('.nc-wishlist-btn');
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick.bind(this));
    this.updateState();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this.onWishlistUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const added = NcWishlistStore.toggle(this.handle);
    this.button.classList.add('nc-wishlist-btn--animate');
    this.button.addEventListener('animationend', () => {
      this.button.classList.remove('nc-wishlist-btn--animate');
    }, { once: true });
  }

  onWishlistUpdate(data) {
    if (data.handle === this.handle) this.updateState();
  }

  updateState() {
    const active = NcWishlistStore.has(this.handle);
    this.button.setAttribute('aria-pressed', active);
  }
}

customElements.define('nc-wishlist-button', NcWishlistButton);

class NcWishlistCounter extends HTMLElement {
  connectedCallback() {
    this.updateCount();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this.updateCount.bind(this));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  updateCount() {
    const count = NcWishlistStore.count();
    this.textContent = count > 0 ? count : '';
    this.toggleAttribute('hidden', count === 0);
  }
}

customElements.define('nc-wishlist-counter', NcWishlistCounter);

class NcWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('#NcWishlistGrid');
    this.empty = this.querySelector('#NcWishlistEmpty');
    this.loading = this.querySelector('#NcWishlistLoading');
    this.loadWishlist();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this.onWishlistUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  async loadWishlist() {
    const items = NcWishlistStore.getAll();
    if (items.length === 0) {
      this.showEmpty();
      return;
    }

    const handles = items.map((item) => item.handle);
    const cards = await this.fetchCards(handles);

    this.loading.setAttribute('hidden', '');
    if (cards.length === 0) {
      this.showEmpty();
      return;
    }

    cards.forEach(({ handle, html }) => this.renderCard(handle, html));
  }

  async fetchCards(handles) {
    const cards = [];
    const batchSize = 4;

    for (let i = 0; i < handles.length; i += batchSize) {
      const batch = handles.slice(i, i + batchSize);
      const results = await Promise.all(
        batch.map((handle) =>
          fetch(`/products/${handle}?section_id=nc-wishlist-card`)
            .then((res) => (res.ok ? res.text() : null))
            .then((html) => (html ? { handle, html } : null))
            .catch(() => null)
        )
      );
      results.forEach((result) => {
        if (result) cards.push(result);
      });
    }
    return cards;
  }

  renderCard(handle, html) {
    const wrapper = document.createElement('li');
    wrapper.className = 'nc-wishlist-card grid__item';
    wrapper.dataset.handle = handle;
    wrapper.innerHTML = html;
    this.grid.appendChild(wrapper);
  }

  onWishlistUpdate(data) {
    if (data.action === 'remove') {
      const card = this.grid.querySelector(`[data-handle="${data.handle}"]`);
      if (card) {
        card.classList.add('nc-wishlist-card--removing');
        card.addEventListener('animationend', () => card.remove(), { once: true });
      }
      if (data.count === 0) {
        setTimeout(() => this.showEmpty(), 300);
      }
    }
  }

  showEmpty() {
    this.loading.setAttribute('hidden', '');
    this.grid.setAttribute('hidden', '');
    this.empty.removeAttribute('hidden');
  }
}

customElements.define('nc-wishlist-page', NcWishlistPage);
